"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.startTour = exports.REPLAY_TOURS = exports.TOURS = exports.NO_TOUR_ID = void 0;
const util_1 = require("@app/util");
const use_onboarding_steps_1 = require("./use-onboarding-steps");
/** stands in for a tour that is configured but whose Intercom id is not known yet */
exports.NO_TOUR_ID = 0;
/**
 * The ids are the numbers Intercom puts in a tour's own URL
 * (`…/outbound/tour/1599986`), which is the same id its share link exposes as
 * `?product_tour_id=`. They are stable for the life of the tour, so they live here rather
 * than in configuration — but note they are per Intercom workspace: if the tours are ever
 * rebuilt in a different one, they all change together.
 */
exports.TOURS = {
    [use_onboarding_steps_1.STEP_KEYS.POSITIONS]: {
        id: 1599986,
        path: '/workspace/job-positions',
        anchor: 'positions-add',
    },
    [use_onboarding_steps_1.STEP_KEYS.EMPLOYEES]: {
        id: 1600006,
        path: '/workspace/employees',
        anchor: 'employees-invite',
    },
    // same page and same two anchors as EMPLOYEES, but its own tour — the difference between
    // "invite someone" and "give them a contract" lives in the tour content, not here
    [use_onboarding_steps_1.STEP_KEYS.CONTRACTS]: {
        id: 1600007,
        path: '/workspace/employees',
        anchor: 'employees-invite',
    },
    // no anchor on purpose: this one opens as a corner tooltip, because the „add" control is
    // the shared @ui Dropdown and pointing at the item INSIDE it would mean teaching that
    // component about tours
    [use_onboarding_steps_1.STEP_KEYS.SCHEDULE]: {
        id: 1600017,
        path: '/schedule',
    },
    /*
     * Shares `/schedule` with the tour above — the path alone cannot tell the two apart,
     * `activeStep` is what does.
     *
     * Its anchor only exists once the workspace has unpublished changes (the announce button
     * is not rendered otherwise), and in workspaces where saving a shift publishes it right
     * away the publish step can go done at the same moment as the schedule step, leaving its
     * „Vyřešit" button never rendered and this tour unreachable. Known and accepted.
     */
    [use_onboarding_steps_1.STEP_KEYS.PUBLISH]: {
        id: 1600009,
        path: '/schedule',
        anchor: 'schedule-publish',
    },
    [use_onboarding_steps_1.STEP_KEYS.TERMINAL]: {
        id: 1600013,
        path: '/backoffice/terminals',
        anchor: 'terminals-add',
    },
    /*
     * TODO: replace `id` with the real Intercom tour id (the number in the tour's own URL,
     * `…/outbound/tour/<id>`). `NO_TOUR_ID` is what keeps `startTour` from handing Intercom a
     * made-up number, which would open whatever tour happens to carry it.
     *
     * Unanchored on purpose until then: the dashboard has no `data-tour` attribute yet, so
     * this opens as a corner tooltip the way the schedule tour does.
     */
    [use_onboarding_steps_1.STEP_KEYS.DASHBOARD]: {
        id: exports.NO_TOUR_ID,
        path: '/dashboard',
    },
};
/**
 * Whether clicking „Vyřešit" on the same step again replays its tour.
 *
 * A constant rather than a setting because it is a product decision, not a per-user one:
 * flip it to `false` to show each tour at most once per session. Intercom cannot own this —
 * `startTour` is an explicit start that bypasses a tour's own audience and show-once rules,
 * which only govern tours Intercom triggers itself.
 */
exports.REPLAY_TOURS = true;
/** steps whose tour has already run, for the `REPLAY_TOURS === false` case */
const shownSteps = new Set();
/**
 * Starts the tour belonging to `step`, if there is one.
 *
 * Best-effort by design: the Intercom snippet is injected outside this repo and is one of
 * the first things an ad blocker removes, so a missing `window.Intercom` is an ordinary
 * state, not an error. It is logged rather than silenced so that a mistyped id or a step
 * missing from `TOURS` is still noticeable in development.
 */
const startTour = (step) => {
    const tour = exports.TOURS[step];
    if ((0, util_1.isUndefined)(tour)) {
        console.warn(`intercom-tour: no tour configured for onboarding step "${step}"`);
        return;
    }
    if (tour.id === exports.NO_TOUR_ID) {
        console.warn(`intercom-tour: no Intercom tour id yet for onboarding step "${step}"`);
        return;
    }
    if (typeof window.Intercom !== 'function') {
        console.warn(`intercom-tour: Intercom is not available, skipping tour for step "${step}"`);
        return;
    }
    if (!exports.REPLAY_TOURS && shownSteps.has(step))
        return;
    shownSteps.add(step);
    // Intercom's own code, which we neither ship nor control, and which cannot be exercised
    // outside production — the snippet is not injected on dev or stage. If it throws, that
    // must not escape: the caller clears the „this tour has run" flag straight after this
    // returns, and skipping that would leave the tour trying again on every navigation back
    // to the same page. A tour that fails to open is a non-event; one that retries forever
    // is a bug the manager can see.
    try {
        window.Intercom('startTour', tour.id);
    }
    catch (e) {
        console.warn(`intercom-tour: Intercom threw while starting the tour for "${step}"`, e);
    }
};
exports.startTour = startTour;
